import { captureException } from '@sentry/react'
import { Link as RouterLink, useHistory } from 'react-router-dom'

import { ClientError, hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Form } from '@tribeplatform/react-components/Form'
import { T } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  SystemSpaceSlug,
  useAuthToken,
  useLogin,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { isMemberNotConfirmed } from '../../lib/app.lib'
import { useQuery } from '../../lib/useQuery'
import { Ssos } from './Ssos'
import { onAuthenticate } from './utils/onAuthenticate'

export const Login = ({ direct = false }) => {
  const history = useHistory()
  const { data: authToken } = useAuthToken() || {}
  const { network } = authToken
  const { redirect } = useRouter()

  const { generatePath } = useSpaceUtil()
  const { login, isLoading } = useLogin()
  const { update } = useRefreshToken()
  const [canJoinNetwork] = hasScopesPermission(network, ['joinNetwork'])

  const query = useQuery()
  const queryString: { redirect?: string; method?: string } = {}
  if (query.redirect) {
    queryString.redirect = query.redirect
  }
  if (query.method) {
    queryString.method = query.method
  }

  const onSubmit = async data => {
    try {
      const _authToken = await login({
        willReload: true,
        variables: {
          input: {
            usernameOrEmail: data?.usernameOrEmail,
            password: data?.password,
          },
        },
      })

      // TODO: remove this line after portal module is completed
      const newAuthToken = await update(_authToken)

      const { refreshToken, accessToken, member, role } = newAuthToken || {}
      if (accessToken || refreshToken) {
        if (!isMemberNotConfirmed(member, role)) {
          onAuthenticate()
          if (accessToken) {
            let redirectUri = query.redirect || '/'
            // prevent external redirects
            if (
              redirectUri.substring(0, 1) !== '/' ||
              redirectUri.substring(0, 2) === '//'
            ) {
              redirectUri = '/'
            }

            if (query?.method === 'mobile') {
              redirect(
                `community://navigate${redirectUri}?mobileToken=${accessToken}`,
              )
            } else {
              setTimeout(() => {
                redirect(redirectUri)
              }, 25)
            }
          }
        } else {
          history.replace(
            generatePath(SystemSpaceSlug.VERIFY_EMAIL, null, {
              memberId: member?.id,
            }),
          )
        }
      }
    } catch (err) {
      // better error message for `usernameOrEmail` field
      const errors = (err as ClientError)?.response?.errors
      errors?.forEach(e => {
        if (e?.code && String(e.code) !== '110') {
          captureException(e, {
            tags: {
              page: 'login',
              action: 'mutation',
            },
          })
        }
        if (e?.field === 'usernameOrEmail' && e?.message) {
          e.message = e.message.replace('usernameOrEmail', 'email')
        }
      })
      throw err
    }
  }

  const showLoginForm = direct ? true : !network.hideDefaultAuthenticationForm
  const showSsos = !direct

  return (
    <>
      <div className="mx-auto w-full max-w-md text-center text-content-on-background">
        <h2 className="mt-6 text-3xl font-extrabold">
          <T defaultMessage="Sign in to your account" id="Auth.Login.Title" />
        </h2>
        {canJoinNetwork && (
          <div className="mt-3 text-lg">
            <T
              defaultMessage="Don’t have an account?"
              id="Auth.Login.SignUpInstruct"
            />{' '}
            <Link
              as={RouterLink}
              to={generatePath(SystemSpaceSlug.SIGNUP, null, queryString)}
            >
              <T
                defaultMessage="Sign up now"
                id="Auth.Login.Actions.SignUpLink"
              />
            </Link>
          </div>
        )}
      </div>

      <div className="mt-8 mx-auto w-full max-w-md flex flex-col space-y-5">
        <Card className="py-8 px-4">
          {showLoginForm && (
            <>
              <Form className="space-y-5" onSubmit={onSubmit}>
                <Form.Input
                  autoFocus
                  autoComplete="email"
                  label={<T defaultMessage="Email" id="Generics.Email" />}
                  name="usernameOrEmail"
                />

                <Form.Input
                  name="password"
                  type="password"
                  label={<T defaultMessage="Password" id="Generics.Password" />}
                  autoComplete="current-password"
                />

                <div className="text-sm text-end">
                  <Link as={RouterLink} to="/auth/forgot-password">
                    <T
                      defaultMessage="Forgot your password?"
                      id="Generics.ForgotYourPassword"
                    />
                  </Link>
                </div>

                <div>
                  <Button
                    fullWidth
                    variant="primary"
                    type="submit"
                    size="lg"
                    loading={isLoading}
                  >
                    <T defaultMessage="Log in" id="Generics.LogIn" />
                  </Button>
                </div>
              </Form>
            </>
          )}
          {showSsos && <Ssos hasDivider={showLoginForm} />}
        </Card>
      </div>
    </>
  )
}
